/** Graph filter toolbar — search, category chips, edge types, similarity toggle, stats. (#177 polish) */

import { useState, useCallback, useEffect, useRef } from 'react'
import { EDGE_TYPES } from '@agentwiki/shared'
import { CATEGORY_COLORS, DEFAULT_NODE_COLOR } from './graph-canvas-styles'

export interface GraphFilters {
  category?: string
  tag?: string
  types?: string[]
  includeImplicit?: boolean
}

interface GraphToolbarProps {
  filters: GraphFilters
  onChange: (filters: GraphFilters) => void
  stats?: { nodeCount: number; edgeCount: number; explicitEdges: number; implicitEdges: number }
  searchValue?: string
  /**
   * Called when the search query changes. MUST be memoized by parent (useCallback)
   * to avoid debounce timer resets on every parent re-render.
   */
  onSearch?: (query: string) => void
  onResetView?: () => void
}

const TRANSITION = 'transition-[background-color,color,transform,border-color,box-shadow] duration-200 ease-out'

const chipBase = `inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${TRANSITION} active:scale-[0.97]`
const chipIdle = 'bg-transparent text-neutral-500 hover:bg-neutral-100/60 dark:hover:bg-white/[0.03] hover:text-neutral-700 dark:text-neutral-300'
const chipActive = 'bg-neutral-200/80 dark:bg-white/[0.06] text-neutral-900 dark:text-neutral-100 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]'

export function GraphToolbar({ filters, onChange, stats, searchValue, onSearch, onResetView }: GraphToolbarProps) {
  const [searchQuery, setSearchQuery] = useState(searchValue ?? '')
  const didMountRef = useRef(false)
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch

  useEffect(() => { setSearchQuery(searchValue ?? '') }, [searchValue])

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    const timer = setTimeout(() => onSearchRef.current?.(searchQuery), 200)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const toggleType = (type: string) => {
    const current = filters.types ?? []
    const updated = current.includes(type) ? current.filter((t) => t !== type) : [...current, type]
    onChange({ ...filters, types: updated.length ? updated : undefined })
  }

  const toggleCategory = useCallback((cat: string) => {
    onChange({ ...filters, category: filters.category === cat ? undefined : cat })
  }, [filters, onChange])

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    onSearchRef.current?.(searchQuery)
  }, [searchQuery])

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
    onSearchRef.current?.('')
  }, [])

  return (
    <div className="border-b border-neutral-200 dark:border-white/[0.06] bg-white/80 dark:bg-surface-1/40 backdrop-blur-sm">
      {/* Row 1 — search · reset · stats (always-visible essentials) */}
      <div className="flex items-center gap-3 px-4 py-2.5">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex-1">
          <div className="group relative max-w-sm">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Find a node by title, tag, or category"
              aria-label="Search graph nodes"
              className={`h-9 w-full rounded-lg border border-neutral-200 dark:border-white/[0.06] bg-white dark:bg-surface-2 pl-9 pr-9 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 outline-none ${TRANSITION} hover:bg-neutral-100 dark:bg-surface-3 focus:border-brand-500/40 focus:bg-white dark:bg-surface-2 focus:ring-2 focus:ring-brand-500/15`}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 group-focus-within:text-neutral-700 dark:text-neutral-300" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
            </svg>
            {searchQuery && (
              <button
                type="button"
                onClick={handleSearchClear}
                aria-label="Clear search"
                className={`absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-neutral-500 ${TRANSITION} hover:bg-neutral-100 dark:bg-surface-3 hover:text-neutral-800 dark:text-neutral-200 active:scale-90`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            )}
          </div>
        </form>

        {/* Reset view — distinct treatment so it reads as a verb, not a chip */}
        {onResetView && (
          <button
            onClick={onResetView}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 dark:border-white/[0.06] bg-transparent px-2.5 py-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-300 ${TRANSITION} hover:border-neutral-400 dark:border-white/[0.1] hover:bg-neutral-100/60 dark:hover:bg-white/[0.02] hover:text-neutral-900 dark:text-neutral-100 active:scale-[0.97]`}
            title="Reset view and clear focus"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/>
            </svg>
            Reset view
          </button>
        )}

        {/* Stats — tabular numerals, medium-weight numbers, light labels */}
        {stats && (
          <div className="ml-auto flex items-center gap-4 font-[var(--font-sans,inherit)] text-[11px] text-neutral-500 tabular-nums">
            <span className="inline-flex items-baseline gap-1">
              <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{stats.nodeCount}</span>
              <span>nodes</span>
            </span>
            <span className="h-3 w-px bg-neutral-200/80 dark:bg-white/[0.06]" aria-hidden="true" />
            <span className="inline-flex items-baseline gap-1">
              <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{stats.explicitEdges}</span>
              <span>links</span>
            </span>
            {stats.implicitEdges > 0 && (
              <>
                <span className="h-3 w-px bg-neutral-200/80 dark:bg-white/[0.06]" aria-hidden="true" />
                <span className="inline-flex items-baseline gap-1">
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{stats.implicitEdges}</span>
                  <span>similar</span>
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Row 2 — filter chips (categories · edges · similarity) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 pb-2.5">
        {/* Category filter chips */}
        <div className="flex items-center gap-1">
          <span className="mr-1 text-[11px] text-neutral-500">Nodes</span>
          {Object.entries(CATEGORY_COLORS).map(([cat, color]) => {
            const active = filters.category === cat
            return (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`${chipBase} ${active ? chipActive : chipIdle}`}
                aria-pressed={active}
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{
                    backgroundColor: color,
                    boxShadow: active ? `0 0 0 2px ${color}33` : undefined,
                  }}
                  aria-hidden="true"
                />
                {cat}
              </button>
            )
          })}
          {(() => {
            const active = filters.category === 'uncategorized'
            return (
              <button
                onClick={() => toggleCategory('uncategorized')}
                className={`${chipBase} ${active ? chipActive : chipIdle}`}
                aria-pressed={active}
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{
                    backgroundColor: DEFAULT_NODE_COLOR,
                    boxShadow: active ? `0 0 0 2px ${DEFAULT_NODE_COLOR}33` : undefined,
                  }}
                  aria-hidden="true"
                />
                other
              </button>
            )
          })()}
        </div>

        <span className="h-3 w-px bg-neutral-200/80 dark:bg-white/[0.06]" aria-hidden="true" />

        {/* Edge type filters */}
        <div className="flex items-center gap-1">
          <span className="mr-1 text-[11px] text-neutral-500">Edges</span>
          {EDGE_TYPES.map((type) => {
            const active = !filters.types?.length || filters.types.includes(type)
            return (
              <button
                key={type}
                onClick={() => toggleType(type)}
                className={`${chipBase} ${active ? chipActive : chipIdle}`}
                aria-pressed={active}
              >
                {type}
              </button>
            )
          })}
        </div>

        <span className="h-3 w-px bg-neutral-200/80 dark:bg-white/[0.06]" aria-hidden="true" />

        {/* Similarity toggle */}
        <label className="inline-flex cursor-pointer items-center gap-2 select-none">
          <span className="relative inline-flex">
            <input
              type="checkbox"
              checked={filters.includeImplicit ?? true}
              onChange={(e) => onChange({ ...filters, includeImplicit: e.target.checked })}
              className="peer sr-only"
            />
            <span
              className={`block h-4 w-7 rounded-full bg-neutral-200/80 dark:bg-white/[0.06] ${TRANSITION} peer-checked:bg-brand-500/40 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/30 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-surface-1`}
            />
            <span
              className={`absolute left-0.5 top-0.5 block h-3 w-3 rounded-full bg-neutral-300 shadow-sm ${TRANSITION} peer-checked:translate-x-3 peer-checked:bg-white`}
            />
          </span>
          <span className="text-[11px] text-neutral-500 dark:text-neutral-400">Similarity links</span>
        </label>
      </div>
    </div>
  )
}
